/**
 * Hook for fetching pipeline stage CRM objects.
 *
 * Fetches both deal pipeline stages and contact lifecycle stages,
 * providing lookup utilities for stage data.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { apiCall } from '@/lib/http/client';
import type { CrmObject, CrmObjectsListResponse } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { convertSumAmounts } from '@/utils/convertCurrency';

import type { CrmObjectTypeSchema } from '../core/SchemaClient';
import { useAssociationTypes } from './useAssociationTypes';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from './useEntityTypeSchema';
import { useExchangeRates } from './useExchangeRates';

/**
 * Multi-currency amount entry (e.g., { amount: 1000, unit: "USD" }).
 */
export interface CurrencyAmount {
  amount: number;
  unit: string;
}

/**
 * Stage data extracted from CRM objects.
 */
export interface Stage {
  entity_id: string;
  label: string; // from ahoy_name property
  // Only populated for contact lifecycle stages — deal pipeline stages return empty string
  pipeline_name: string; // from ahoy_pipeline property
  probability: number; // from ahoy_probability property
  order: number; // from ahoy_order property
  color?: string; // from ahoy_color property
  entity_type_name: string;
  // Rollup properties (only available on deal pipeline stages) - kept for backward compatibility
  dealCount?: number;
  potentialDealTotal?: CurrencyAmount[];
  weightedDealTotal?: CurrencyAmount[];
  /** Converted single-currency total when rates are available */
  potentialDealTotalConverted?: { amount: number; unit: string };
  weightedDealTotalConverted?: { amount: number; unit: string };
  // Generic properties field for extensibility
  properties?: Record<string, unknown>;
}

/**
 * Schema association type for association type discovery.
 */
interface AssociationType {
  association_type_name: string;
  source_entity_type_name: string;
  target_entity_type_name: string;
  cardinality: string;
}

/**
 * Discover stage entity types from association types.
 * Uses entity_kind from object types to identify stage types,
 * then finds many_to_one associations targeting those types.
 * Also identifies pipeline stage types (stages associated with a pipeline type).
 */
export function discoverStageTypes(
  objectTypes: CrmObjectTypeSchema[],
  associationTypes: AssociationType[]
): {
  mapping: Record<string, string>;
  pipelineStageTypeNames: Set<string>;
} {
  const mapping: Record<string, string> = {};
  const pipelineStageTypeNames = new Set<string>();

  // Guard against null/undefined
  if (!associationTypes || !Array.isArray(associationTypes)) {
    return { mapping, pipelineStageTypeNames };
  }

  // Build sets of stage and pipeline entity type names using entity_kind
  const stageTypeNames = new Set<string>(
    objectTypes
      .filter(ot => ot.entity_kind.includes('stage'))
      .map(ot => ot.entity_type_name)
  );
  const pipelineTypeNames = new Set<string>(
    objectTypes
      .filter(ot => ot.entity_kind.includes('pipeline'))
      .map(ot => ot.entity_type_name)
  );

  for (const assoc of associationTypes) {
    if (
      assoc.cardinality === 'many_to_one' &&
      !assoc.source_entity_type_name.endsWith('_timeline') &&
      !assoc.target_entity_type_name.endsWith('_timeline') &&
      !assoc.association_type_name.endsWith('_timeline')
    ) {
      // Map entity types to their stage types
      if (stageTypeNames.has(assoc.target_entity_type_name)) {
        mapping[assoc.source_entity_type_name] = assoc.target_entity_type_name;
      }
      // Identify pipeline stages: stage types that have a many_to_one association to a pipeline type
      if (
        stageTypeNames.has(assoc.source_entity_type_name) &&
        pipelineTypeNames.has(assoc.target_entity_type_name)
      ) {
        pipelineStageTypeNames.add(assoc.source_entity_type_name);
      }
    }
  }

  return { mapping, pipelineStageTypeNames };
}

/**
 * Return true iff the entity type is pipeline-rendering, using the declarative
 * kind rule: its entity_kind includes "case".
 *
 * This mirrors the backend rule and replaces the former association walk.
 * ahoy_deal carries the "case" kind. ahoy_contact (lifecycle stages, not a
 * pipeline) is excluded. Fails closed (returns false) when entity_kind is
 * undefined/empty or the type is absent from objectTypes.
 */
export function entityTypeHasPipeline(
  entityTypeName: string,
  objectTypes: Array<
    Pick<CrmObjectTypeSchema, 'entity_type_name' | 'entity_kind'>
  >
): boolean {
  const entityKind =
    objectTypes.find(t => t.entity_type_name === entityTypeName)?.entity_kind ??
    [];
  return entityKind.includes('case');
}

/**
 * Parse a multi-currency value from the API response.
 * Multi-currency properties come back as arrays of { amount, unit } objects.
 */
function parseCurrencyAmounts(
  raw: Array<{ amount: number | string; unit: string }> | undefined
): CurrencyAmount[] | undefined {
  if (!raw || !Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  return raw.map(item => ({
    amount:
      typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount,
    unit: item.unit,
  }));
}

/**
 * Transform a CRM object into a Stage.
 */
export function transformToStage(obj: CrmObject): Stage {
  const props = obj.properties ?? {};
  const stage: Stage = {
    entity_id: obj.entity_id,
    label: getPropertyValue<string>(props.ahoy_name) || '',
    pipeline_name: getPropertyValue<string>(props.ahoy_pipeline) || '',
    probability: getPropertyValue<number>(props.ahoy_probability) ?? 0,
    order: getPropertyValue<number>(props.ahoy_order) ?? 0,
    color: getPropertyValue<string>(props.ahoy_color) || undefined,
    entity_type_name: obj.entity_type_name,
    properties: {},
  };

  // Extract rollup properties for deal pipeline stages (backward compatibility)
  if (obj.entity_type_name === 'ahoy_deal_pipeline_stage') {
    const rawDealCount = getPropertyValue<number | string>(
      props.ahoy_deal_count
    );
    if (rawDealCount != null) {
      stage.dealCount = Math.round(Number(rawDealCount) || 0);
    }

    const potentialTotal = getPropertyValue<
      Array<{ amount: number | string; unit: string }>
    >(props.ahoy_potential_deal_total);
    stage.potentialDealTotal = parseCurrencyAmounts(potentialTotal);

    const weightedTotal = getPropertyValue<
      Array<{ amount: number | string; unit: string }>
    >(props.ahoy_weighted_deal_total);
    stage.weightedDealTotal = parseCurrencyAmounts(weightedTotal);
  }

  // Store all raw properties for extensibility
  if (props && stage.properties) {
    for (const [key, value] of Object.entries(props)) {
      stage.properties[key] = getPropertyValue(value);
    }
  }

  return stage;
}

/**
 * Fetch stages of a specific type from the CRM.
 */
export async function fetchStagesByType(
  orgId: number,
  stageType: string
): Promise<Stage[]> {
  const url = `/api/orgs/${orgId}/crm-events/crm-objects/query/`;

  // Base properties for all stages.
  //
  // Note: we do NOT request ahoy_pipeline. On `ahoy_deal_pipeline_stage`
  // it's a deprecated text property retained only for historical event
  // replay (see deal_pipeline_stage_properties.yaml). On custom stage
  // kinds it is not declared at all. The canonical stage→pipeline link
  // is the `{stage_type}_to_{pipeline_type}` association — query that
  // when you need to map a stage to its pipeline.
  const properties = ['ahoy_name', 'ahoy_order', 'ahoy_color'];

  // Deal pipeline stages have additional properties
  if (stageType === 'ahoy_deal_pipeline_stage') {
    properties.push('ahoy_probability');
    // Rollup properties for pipeline metrics
    properties.push(
      'ahoy_deal_count',
      'ahoy_potential_deal_total',
      'ahoy_weighted_deal_total'
    );
  }

  const response = await apiCall<CrmObjectsListResponse>(
    url,
    'POST',
    {
      entity_type_name: stageType,
      properties,
      sort: 'ahoy_order',
      order: 'asc',
      limit: 100, // Stages are typically few, but set a reasonable limit
    },
    30000
  );

  return response.results.map(obj => transformToStage(obj));
}

/**
 * Fetch all stages (dynamically discovered from schema).
 */
async function fetchAllStages(
  orgId: number,
  objectTypes: CrmObjectTypeSchema[],
  associationTypes: AssociationType[]
): Promise<{
  stages: Stage[];
  mapping: Record<string, string>;
  stageTypeNames: Set<string>;
}> {
  // Discover stage types from schema
  const { mapping } = discoverStageTypes(objectTypes, associationTypes);

  // Build set of stage type names from mapping values
  const stageTypeNames = new Set(Object.values(mapping));

  // Get unique stage entity types
  const stageTypes = Array.from(stageTypeNames);

  // Fetch stages for each discovered type
  const stageArrays = await Promise.all(
    stageTypes.map(stageType => fetchStagesByType(orgId, stageType))
  );

  return {
    stages: stageArrays.flat(),
    mapping,
    stageTypeNames,
  };
}

/**
 * Hook to fetch ALL pipeline stages across every stage type in the org.
 *
 * Performs schema-driven fan-out: discovers every stage entity type from
 * the object-type schema and fetches the full list of stages for each
 * type. This is expensive (N+1 fetches) and is ONLY appropriate for
 * genuine cross-pipeline use cases that span entity types — e.g., resolving
 * stage labels for arbitrary entity_ids, or aggregating across all proposals.
 *
 * For most callers — use one of these instead:
 *   - usePipelineStages(orgId, baseEntityType) — focused on a single
 *     entity's stages, no fan-out across unrelated stage types.
 *   - useStageById(orgId, entityId) — resolve a single stage by id.
 *   - useStageTypeNames(orgId) — when you only need the Set of stage
 *     entity type names (no stage data).
 *
 * Current legitimate callers: ProposalCenter, useSchemaLabels.
 *
 * @param orgId - Org ID
 * @returns React Query result with stages and helper functions
 *
 * @example
 * const { stages, getStageByEntityId, isLoading } = useStages(orgId);
 */
export function useStages(orgId: number | undefined) {
  const { currentOrg } = useOrg();
  const targetCurrency = currentOrg?.defaultCurrency;

  const {
    associationTypes,
    isLoading: isAssociationTypesLoading,
    error: associationTypesError,
    refetch: refetchAssociationTypes,
  } = useAssociationTypes(orgId);

  const { data: ratesData } = useExchangeRates(orgId);

  // Fetch object types using the shared cache key to avoid duplicate API calls
  const {
    data: objectTypesData,
    error: objectTypesError,
    refetch: refetchObjectTypes,
  } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId as number),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes - matches useEntityTypeSchema
  });

  const objectTypes = useMemo(() => objectTypesData ?? [], [objectTypesData]);

  // Derive a stable signature from object types so schema changes (e.g., updated
  // entity_kind or renamed types) invalidate the stages query even if count is unchanged.
  const objectTypesSignature = useMemo(
    () =>
      objectTypes
        .map(t => `${t.entity_type_name}:${(t.entity_kind ?? []).join(',')}`)
        .sort()
        .join('|'),
    [objectTypes]
  );

  const associationTypesSignature = useMemo(
    () =>
      associationTypes
        .map(
          a =>
            `${a.association_type_name}:${a.source_entity_type_name}:${a.target_entity_type_name}:${a.cardinality}`
        )
        .sort()
        .join('|'),
    [associationTypes]
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'stages',
      orgId,
      objectTypesSignature,
      associationTypesSignature,
    ],
    queryFn: async () => {
      if (!orgId) {
        return { stages: [], mapping: {}, stageTypeNames: new Set<string>() };
      }
      return fetchAllStages(orgId, objectTypes, associationTypes);
    },
    enabled: !!orgId && objectTypes.length > 0 && !isAssociationTypesLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes - stages rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes cache time
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Stabilize: prevents infinite re-renders from unstable fallback array/object identity
  const stableStages = useMemo(() => data?.stages ?? [], [data?.stages]);
  const stableMapping = useMemo(() => data?.mapping ?? {}, [data?.mapping]);
  const stableStageTypeNames = useMemo(
    () => data?.stageTypeNames ?? new Set<string>(),
    [data?.stageTypeNames]
  );

  // True when the org expects conversion (has a defaultCurrency) but rates are
  // not yet available. Callers must check this before rendering any
  // single-currency converted total.
  const ratesUnavailable = !!targetCurrency && !ratesData?.rates;

  const stagesWithConverted = useMemo(() => {
    const rates = ratesData?.rates ?? null;
    const withConverted = stableStages.map(stage => {
      if (
        !targetCurrency ||
        !rates ||
        stage.entity_type_name !== 'ahoy_deal_pipeline_stage'
      ) {
        return stage;
      }
      return {
        ...stage,
        potentialDealTotalConverted: convertSumAmounts(
          stage.potentialDealTotal,
          targetCurrency,
          rates
        ),
        weightedDealTotalConverted: convertSumAmounts(
          stage.weightedDealTotal,
          targetCurrency,
          rates
        ),
      };
    });
    return withConverted;
  }, [stableStages, targetCurrency, ratesData]);

  /**
   * Map of stages by entity_id for O(1) lookup.
   */
  const stagesByEntityId = useMemo(() => {
    const map = new Map<string, Stage>();
    for (const stage of stagesWithConverted) {
      map.set(stage.entity_id, stage);
    }
    return map;
  }, [stagesWithConverted]);

  /**
   * Get a stage by its entity ID.
   */
  const getStageByEntityId = useCallback(
    (entityId: string): Stage | undefined => {
      return stagesByEntityId.get(entityId);
    },
    [stagesByEntityId]
  );

  /**
   * Get all stages for a specific CRM entity type (e.g., ahoy_deal, ahoy_contact).
   * Uses dynamically discovered mapping from object type to stage type.
   */
  const getStagesForObjectType = useCallback(
    (entityType: string): Stage[] => {
      const stageType = stableMapping[entityType];
      if (!stageType) {
        return [];
      }
      return stagesWithConverted
        .filter(stage => stage.entity_type_name === stageType)
        .sort((a, b) => a.order - b.order);
    },
    [stagesWithConverted, stableMapping]
  );

  /**
   * Get all stages of a specific stage entity type.
   * Use this for generic stage lookups (e.g., from association target type).
   */
  const getStagesByType = useCallback(
    (stageEntityTypeName: string): Stage[] => {
      return stagesWithConverted
        .filter(stage => stage.entity_type_name === stageEntityTypeName)
        .sort((a, b) => a.order - b.order);
    },
    [stagesWithConverted]
  );

  // Combined refetch that covers all three dependency chains (object-types,
  // association-types, and stages) so callers can recover from a failure in
  // any of them via the public hook API.
  const refetchAll = useCallback(async () => {
    await Promise.all([refetchObjectTypes(), refetchAssociationTypes()]);
    await refetch();
  }, [refetchObjectTypes, refetchAssociationTypes, refetch]);

  return {
    stages: stagesWithConverted,
    ratesUnavailable,
    stageTypeNames: stableStageTypeNames,
    isLoading,
    error: error ?? objectTypesError ?? associationTypesError,
    refetch: refetchAll,
    getStagesForObjectType,
    getStagesByType,
    getStageByEntityId,
    stagesByEntityId,
  };
}
